import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { ReactComponent as SendIcon } from '../../assets/icons/FeatherSend.svg';
import { ReactComponent as IconPassword } from '../../assets/icons/IconPassword.svg';
import { Button, Input, NavHeader } from '../../components';
import InputCode from '../../components/Inputs/InputCod';
import { sendCodeEmail } from '../../controller/EmailController';
import { UserController } from '../../controller/UserController';
import { useAlert } from '../../hooks/useAlert';
import { encryptPassword } from '../../utils/encryptPassword';
import { validateEmail } from '../../utils/validateEmail';
import {
  CodRecovery,
  ColContent,
  Container,
  ContainerNewPassword,
  Content,
  NewPassword,
  SendEmail,
} from './styles';
import { decryptCode } from '../../utils/crypto';
import { useApp } from '../../context';
import { useConfig } from '../../context/configCustom';
import JoyrideTour from '../../components/JoyrideTour';
import TitlePage from '../../components/TitlePage';
import usePersistedState from '../../hooks/usePersistedState';
import { Tooltip } from '@mui/material';
import { TutorialType } from '../../types/enums';
import { useTutorialProgress } from '../../context/tutorialProgress';

export function RecoveryPassword() {
  const TIMER = 60000;
  const { pathResourceEmail } = useConfig();
  const { tutorialProgress } = useTutorialProgress();
  const app = useApp();
  const alert = useAlert();
  const navigate = useNavigate();
  const customAlert = useAlert();
  const [activeFormNewPassword, setActiveFormNewPassword] = useState(false);
  const [activeForm, setActiveForm] = useState(false);
  const [code, setCode] = useState('');
  const [expectedCode, setExpectedCode] = useState('');
  const [sentEmail, setSentEmail] = useState(false);
  const [runningTutorial, setRunningTutorial] = useState(false);
  const defaultValues = {
    email: '',
  };
  const [secondsRemainingToResend, setSecondsRemainingToResend] = useState(TIMER / 1000);
  const [dateToSend, setDateToSend] = usePersistedState<number | undefined>(
    'dateToSend',
    undefined,
  );
  const [disableResend, setDisableResend] = useState(
    dateToSend === undefined ? false : dateToSend + TIMER > new Date().getTime(),
  );
  const defaultValuesPassword = {
    newPassword: '',
    confirmPassword: '',
  };

  const steps = [
    {
      target: '#start-item',
      content:
        'Esta página é destinada a recuperação de senha. Siga o passo a passo para recuperar sua senha.',
      disableBeacon: true,
    },
    {
      target: '#email-input',
      content: 'Insira o e-mail cadastrado.',
    },
    {
      target: '#send-button',
      content:
        'Clique no botão enviar para receber o código de recuperação. Aguarde alguns instantes e verifique seu e-mail.',
    },
    {
      target: '#code',
      content: 'Insira o código de recuperação enviado para o seu e-mail.',
    },
    {
      target: '#otp-button',
      content: 'Clique no botão verificar para validar o código de recuperação.',
    },
    {
      target: '#new-password',
      content: 'Insira a nova senha.',
    },
    {
      target: '#confirm-password',
      content: 'Confirme a nova senha.',
    },
    {
      target: '#confirm-button',
      content: 'Confirme a alteração da senha.',
    },
  ];
  const validationEmailSchema = yup.object().shape({
    email: yup
      .string()
      .email('Digite um e-mail válido')
      .required('O campo e-mail é obrigatório')
      .test('emailUnregistered', 'E-mail não cadastrado', async (email: string) => {
        try {
          if (!validateEmail(email)) return false;
          const resultEmail = await UserController.findByEmail(email, false);
          return resultEmail;
        } catch {
          return false;
        }
      }),
  });

  const validationPasswordSchema = yup.object().shape({
    newPassword: yup
      .string()
      .required('O campo senha é obrigatório')
      .min(6, 'Senha deve ter no mínimo 6 dígitos'),
    confirmPassword: yup.string().oneOf([yup.ref('newPassword'), null], 'Senhas não coincidem'),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    getValues,
  } = useForm({
    defaultValues,
    reValidateMode: 'onSubmit',
    resolver: yupResolver(validationEmailSchema),
  });

  const formPassword = useForm({
    defaultValues: defaultValuesPassword,
    resolver: yupResolver(validationPasswordSchema),
  });

  const handleSendEmail = async () => {
    if (!pathResourceEmail) {
      customAlert.show({
        type: 'error',
        message: 'Não foi possível enviar o e-mail, tente novamente!',
      });
      return;
    }
    app.setLoading(true);
    sendCodeEmail({
      pathResource: pathResourceEmail,
      to: getValues('email'),
      type: 'resetPassword',
    })
      .then(async (resCode) => {
        setExpectedCode(decryptCode(resCode));
        setSentEmail(true);
        setActiveForm(true);
      })
      .catch(() => {
        customAlert.show({
          type: 'error',
          message: 'Não foi possível enviar o e-mail, tente novamente!',
        });
      })
      .finally(() => app.setLoading(false));
  };

  const handleResendCode = () => {
    if (!pathResourceEmail) {
      alert.show({
        type: 'error',
        message: 'Não foi possível enviar o código de verificação, tente novamente!',
      });
      return;
    }
    setDateToSend(new Date().getTime());
    app.setLoading(true);

    sendCodeEmail({
      type: 'resetPassword',
      to: getValues('email'),
      pathResource: pathResourceEmail,
    })
      .then((resCode) => {
        setExpectedCode(decryptCode(resCode));
        setSentEmail(true);
      })
      .catch(() => {
        alert.show({
          type: 'error',
          message: 'Não foi possível enviar o e-mail, tente novamente!',
        });
      })
      .finally(() => app.setLoading(false));
  };

  const verifyCode = () => {
    if (code === expectedCode && code !== '') {
      setActiveFormNewPassword(true);
      customAlert.show({ type: 'success', message: 'Código valido, insira uma nova senha' });
    } else {
      customAlert.show({ type: 'error', message: 'Código inválido' });
    }
  };

  const resetPassword = async () => {
    const formData = new FormData();
    const email = getValues('email');
    const newPassword = formPassword.getValues('newPassword');
    formData.append('email', email);
    formData.append('password', encryptPassword(newPassword));
    UserController.resetPassword(formData)
      .then(() => {
        customAlert.show({ type: 'success', message: 'Senha alterada com sucesso!' });
        navigate('/login');
      })
      .catch(() => customAlert.show({ type: 'error', message: 'Tente novamente mais tarde!' }));
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (dateToSend !== undefined) {
      interval = setInterval(() => {
        const isDisabled = dateToSend + TIMER > new Date().getTime();
        setDisableResend(isDisabled);
        setSecondsRemainingToResend((prev) => prev - 1);
        if (!isDisabled) {
          setSecondsRemainingToResend(TIMER / 1000);
          setDateToSend(undefined);
          clearInterval(interval);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [dateToSend, setDateToSend]);

  useEffect(() => {
    if (!tutorialProgress?.includes(TutorialType.PASSWORD_RECOVERY)) {
      setRunningTutorial(true);
    }
  }, [tutorialProgress]);
  return (
    <>
      <JoyrideTour
        type={TutorialType.PASSWORD_RECOVERY}
        steps={steps}
        run={runningTutorial}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <NavHeader typeHeader="default" />
      <Container>
        <div>
          <div className="page-title">
            <TitlePage
              title="Recuperação de senha"
              onRequestOpen={() => {
                setRunningTutorial(true);
              }}
              id="start-item"
            />
          </div>
        </div>
        <Content>
          <div className="ContentItens">
            <ColContent>
              <SendEmail>
                <div className="txtSend">
                  <div>
                    <SendIcon />
                  </div>
                  <p>
                    Para iniciar a recuperação de senha, coloque o seu e-mail cadastrado para
                    enviarmos um código de recuperação.
                  </p>
                </div>
                <form onSubmit={handleSubmit(handleSendEmail)} className="formSend">
                  <div className="inputSend">
                    <Input
                      id="email-input"
                      name="email"
                      control={control}
                      type="text"
                      placeholder="E-mail"
                      errorText={errors.email?.message}
                      variant="standard"
                      fullWidth
                      error={!!errors.email}
                    />
                    <Button
                      text={
                        disableResend
                          ? `Reenviar código (${secondsRemainingToResend})`
                          : 'Reenviar código'
                      }
                      dimensions={{ width: '10rem', height: '1.5rem' }}
                      disabled={disableResend || !sentEmail}
                      background="transparent"
                      color="primary"
                      textHoverColor="secondary"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        textDecoration: 'underline',
                        padding: 0,
                      }}
                      font={{ size: '1.5rem', weight: 500 }}
                      onClick={handleResendCode}
                    />
                  </div>
                  <div className="btnSend">
                    <Button
                      id="send-button"
                      type="submit"
                      text="ENVIAR"
                      disabled={sentEmail}
                      dimensions={{ width: 144, height: 25 }}
                      background="primary"
                      font={{ size: '0.8rem' }}
                    />
                  </div>
                </form>
              </SendEmail>
              <CodRecovery className={`${activeForm === false ? 'disabled' : ''}`}>
                <Tooltip
                  title={
                    activeForm
                      ? ''
                      : 'Envie o código de recuperação para o seu e-mail, para habilitar a validação por código.'
                  }
                  followCursor>
                  <div className="ContentInputs">
                    <div className="DivTitle">
                      <div className="div">
                        <h1>@</h1>
                      </div>
                      <div>
                        <h2>Veja seu e-mail</h2>
                        <p>Insira o código de recuperação</p>
                      </div>
                    </div>

                    <div className="inputsCod">
                      <div className="inputs">
                        <InputCode
                          id="code"
                          disabled={!activeForm}
                          length={4}
                          onComplete={(c) => setCode(c)}
                        />
                      </div>
                      <div className="BtnVerify">
                        <Button
                          id="otp-button"
                          type="submit"
                          text="VERIFICAR"
                          disabled={!activeForm}
                          dimensions={{ width: 144, height: 25 }}
                          background="primary"
                          font={{ size: '0.8rem' }}
                          onClick={verifyCode}
                        />
                      </div>
                    </div>
                  </div>
                </Tooltip>
              </CodRecovery>
            </ColContent>
            <ContainerNewPassword>
              <Tooltip
                title="Verifique o código de recuperação para habilitar a nova senha"
                followCursor>
                <NewPassword className={`${activeFormNewPassword === false ? 'disabled' : ''}`}>
                  <div className="Title">
                    <IconPassword />
                    <h2>Nova Senha</h2>
                  </div>
                  <form
                    className="InputPassword"
                    onSubmit={formPassword.handleSubmit(resetPassword)}>
                    <div>
                      <Input
                        id="new-password"
                        type="password"
                        control={formPassword.control}
                        placeholder="Senha"
                        name="newPassword"
                        errorText={formPassword.formState.errors.newPassword?.message}
                        disabled={!activeFormNewPassword}
                        variant="standard"
                        fullWidth
                        error={!!formPassword.formState.errors.newPassword}
                      />

                      <Input
                        id="confirm-password"
                        type="password"
                        control={formPassword.control}
                        placeholder="Confirmar Senha"
                        name="confirmPassword"
                        errorText={formPassword.formState.errors.confirmPassword?.message}
                        disabled={!activeFormNewPassword}
                        variant="standard"
                        fullWidth
                        error={!!formPassword.formState.errors.confirmPassword}
                      />
                    </div>
                    <Button
                      id="confirm-button"
                      className="btnUpdatePassword"
                      type="submit"
                      text="ATUALIZAR SENHA"
                      disabled={!activeFormNewPassword}
                      dimensions={{ width: 144, height: 25 }}
                      background="primary"
                      font={{ size: '0.8rem' }}
                      onClick={verifyCode}
                    />
                  </form>
                </NewPassword>
              </Tooltip>
            </ContainerNewPassword>
          </div>
        </Content>
      </Container>
    </>
  );
}
